import { Icon } from '@elkaso-app/web-design';
import { ability } from 'configs/ability';
import { useFeatureFlags } from 'feature-flags';
import { AggSheetIcon } from 'layout/components/sidebar/icons/agg-sheet-icon';
import { AnalyticsIcon } from 'layout/components/sidebar/icons/analytics-icon';
import { BenchmarkingIcon } from 'layout/components/sidebar/icons/benchmarking-icon';
import { CatalogIcon } from 'layout/components/sidebar/icons/catalog-icon';
import { CategoriesIcon } from 'layout/components/sidebar/icons/categories-icon';
import { CountiesIcon } from 'layout/components/sidebar/icons/counties-icon';
import { ElkasoUsersIcon } from 'layout/components/sidebar/icons/elkaso-users-icon';
import { ErpMappingsIcons } from 'layout/components/sidebar/icons/erp-mappings-icons';
import { OrdersIcons } from 'layout/components/sidebar/icons/orders-icons';
import { OtpIcon } from 'layout/components/sidebar/icons/otp-icon';
import { PosIntegrationsIcons } from 'layout/components/sidebar/icons/pos-integrations-icons';
import { PreferencesIcon } from 'layout/components/sidebar/icons/preferences-icon';
import { ProductIcon } from 'layout/components/sidebar/icons/product-icon';
import { PromotionsIcon } from 'layout/components/sidebar/icons/promotions-icon';
import { RestaurantTransfersIcons } from 'layout/components/sidebar/icons/restaurant-transfers-icons';
import { RestaurantsIcons } from 'layout/components/sidebar/icons/restaurants-icons';
import { StatisticsIcon } from 'layout/components/sidebar/icons/statistics-icon';
import { SuppliersIcon } from 'layout/components/sidebar/icons/suppliers-icon';
import { TicketIcon } from 'layout/components/sidebar/icons/ticket-icon';
import { UserManagementIcon } from 'layout/components/sidebar/icons/user-management-icon';
import { WhatsAppIcon } from 'layout/components/sidebar/icons/whatsApp-icon';
import { FiDollarSign, FiFileText, FiSearch, FiShoppingCart } from 'react-icons/fi';

export type TSidebarItem = {
  name: string;
  path: string;
  icon: typeof Icon;
  children?: {
    name: string;
    path: string;
    disable?: boolean;
    group?: string; // New: Group identifier for better organization
  }[];
  hasSocket?: boolean;
  disable?: boolean;
};

export type TSidebarGroup = {
  type: 'group';
  title: string;
  items: TSidebarItem[];
};

export type TSidebarDivider = {
  type: 'divider';
  label?: string;
};

export type TSidebarElement = TSidebarItem | TSidebarGroup | TSidebarDivider;

const BnplIcon = (props: any) => <Icon as={FiDollarSign} width='22px' height='22px' fill='#757575' {...props} />;
const InvoiceScanIcon = (props: any) => <Icon as={FiFileText} width='22px' height='22px' fill='#757575' {...props} />;
const SupplierIntelligenceIcon = (props: any) => (
  <Icon as={FiSearch} width='22px' height='22px' fill='#757575' {...props} />
);
const GpoIcon = (props: any) => <Icon as={FiShoppingCart} width='22px' height='22px' fill='#757575' {...props} />;

export const useSidebarItems = () => {
  const {
    is_cms_v1_11_0_enabled,
    enable_bnpl,
    enable_invoice_scanning,
    enable_supplier_intelligence,
    is_pretzel_match_maker_enabled,
    is_master_products_enabled,
    is_unmatched_demand_enabled,
    is_supplier_profiles_enabled,
    is_product_attributes_enabled,
    is_product_brands_enabled,
    is_reconciliation_enabled,
    is_gpo_prices_enabled,
    is_exclusive_deals_enabled,
    is_sales_lookup_enabled,
    is_gpo_orders_enabled,
    is_price_imports_enabled,
  } = useFeatureFlags();

  const manageAggregatedSheets = ability.can('manage', 'AggregatedSheet');
  const manageOrders = ability.can('manage', 'Order');
  const manageAll = ability.can('manage', 'all');

  const sidebarItems: TSidebarItem[] = [
    {
      name: 'sidebar.statistics',
      path: '/app/statistics',
      icon: StatisticsIcon,
    },
    {
      name: 'sidebar.crm',
      path: '/app/crm',
      icon: PreferencesIcon,
      children: [
        // 🏢 Brand Management
        {
          name: 'Brands',
          path: '/app/crm-brands/list',
          group: 'brand-management',
        },
        {
          name: 'Brand Locations',
          path: '/app/crm-brand-locations/list',
          group: 'brand-management',
        },
        {
          name: 'Source Brand Locations',
          path: '/app/crm-source-brand-locations/list',
          group: 'brand-management',
        },
        // 📞 Contact Management
        {
          name: 'Brand Contact Details',
          path: '/app/crm-brand-contact-details/list',
          group: 'contact-management',
        },
        {
          name: 'Supplier Contact Quotas',
          path: '/app/crm-supplier-contact-quotas/list',
          group: 'contact-management',
        },
        // 📄 Licensing & Legal
        {
          name: 'Trade Licenses [DED]',
          path: '/app/crm-ded-details/list',
          group: 'licensing',
        },
        {
          name: 'Brand Trade License Mappings',
          path: '/app/crm-brand-trade-license-mappings/list',
          group: 'licensing',
        },
        // 🍽️ Cuisine Management
        {
          name: 'Master Cuisines',
          path: '/app/crm-master-cuisines/list',
          group: 'cuisine-management',
        },
        {
          name: 'Cuisines Mappings',
          path: '/app/crm-cuisines/list',
          group: 'cuisine-management',
        },
        // 🔐 Access Control
        {
          name: 'Access Control Management',
          path: '/app/crm-access-control-management/list',
          group: 'access-control',
        },
      ],
    },
    {
      name: 'sidebar.analytics',
      path: '/app/analytics',
      icon: AnalyticsIcon,
      children: [
        {
          name: 'Orders Dashboard',
          path: '/app/analytics/orders/dashboard',
        },
        {
          name: 'Transfers Dashboard',
          path: '/app/analytics/transfers/dashboard',
        },
        {
          name: 'Restaurants Dashboard',
          path: '/app/analytics/restaurants/dashboard',
        },
        {
          name: 'Suppliers Dashboard',
          path: '/app/analytics/suppliers/dashboard',
        },
        {
          name: 'Products Dashboard',
          path: '/app/analytics/products/dashboard',
        },
      ],
      disable: !manageOrders,
    },
    {
      name: 'sidebar.benchmarking',
      path: '/app/benchmarking',
      icon: BenchmarkingIcon,
    },
    {
      name: 'sidebar.restaurants',
      path: '/app/restaurants',
      icon: RestaurantsIcons,
      children: [
        {
          name: 'Restaurants',
          path: '/app/restaurants/list',
        },
        {
          name: 'Documents',
          path: '/app/restaurants/documents',
        },
      ],
    },
    {
      name: 'sidebar.suppliers',
      path: '/app/suppliers',
      icon: SuppliersIcon,
    },
    {
      name: 'Supplier Intelligence',
      path: '/app/supplier-intelligence',
      icon: SupplierIntelligenceIcon,
      disable: !enable_supplier_intelligence,
    },
    {
      name: 'sidebar.products',
      path: '/app/products',
      icon: ProductIcon,
      disable: !manageAll,
    },
    {
      name: 'sidebar.aggSheetRelations',
      path: '/app/agg-sheet-relations',
      icon: AggSheetIcon,
      disable: !manageAggregatedSheets,
    },
    {
      name: 'sidebar.catalogManagement',
      path: '/app/catalog',
      icon: CatalogIcon,
      children: [
        {
          name: 'Food Universe',
          path: '/app/catalog/food-universe',
          disable: true,
        },
        {
          name: 'Product Categories',
          path: '/app/catalog/product-categories',
        },
        {
          name: 'Product Attributes',
          path: '/app/catalog/product-attributes',
          disable: !is_product_attributes_enabled,
        },
        {
          name: 'Product Brands',
          path: '/app/catalog/product-brands',
          disable: !is_product_brands_enabled,
        },
      ],
    },
    {
      name: 'sidebar.ticketManagement',
      path: '/app/ticket',
      icon: TicketIcon,
    },
    {
      name: 'sidebar.promotions',
      path: '/app/promotions',
      icon: PromotionsIcon,
      children: [
        {
          name: 'Today’s Deals',
          path: '/app/promotions/list',
        },
        {
          name: 'Campaign',
          path: '/app/promotions/campaigns',
        },
      ],
    },
    {
      name: 'sidebar.elkasoUsers',
      path: '/app/elkasoUsers',
      icon: ElkasoUsersIcon,
    },
    {
      name: 'sidebar.orders',
      path: '/app/orders',
      icon: OrdersIcons,
    },
    {
      name: 'sidebar.restaurantTransfers',
      path: '/app/restaurant-transfers',
      icon: RestaurantTransfersIcons,
    },
    {
      name: 'sidebar.posIntegrations',
      path: '/app/pos-integrations',
      icon: PosIntegrationsIcons,
      children: [
        {
          name: 'POS Integrations',
          path: '/app/pos-integrations/list',
        },
        {
          name: 'POS issues',
          path: '/app/pos-integrations/issues',
        },
      ],
    },
    {
      name: 'sidebar.erpMappings',
      path: '/app/erp-mappings',
      icon: ErpMappingsIcons,
    },
    {
      name: 'sidebar.categories',
      path: '/app/categories',
      icon: CategoriesIcon,
    },
    {
      name: 'sidebar.documentTypes',
      path: '/app/document-types',
      icon: CategoriesIcon,
      disable: !manageAll,
    },
    {
      // Named per the Pretzel Match Maker design handoff (Kaso Club section).
      name: 'Kaso Club',
      path: '/app/gpo',
      icon: GpoIcon,
      children: [
        // Superseded by the Prices tool — hidden on staging/production while
        // CMS users migrate; kept in development for reference.
        { name: 'Exclusive Deals', path: '/app/exclusive-deals/list', disable: !is_exclusive_deals_enabled },
        { name: 'Pretzel Match Maker', path: '/app/inquiry-processor/list', disable: !is_pretzel_match_maker_enabled },
        {
          name: 'Master Products',
          path: '/app/inquiry-processor/master-products',
          disable: !is_master_products_enabled,
        },
        {
          name: 'Unmatched Demand',
          path: '/app/unmatched-demand/list',
          disable: !is_unmatched_demand_enabled,
        },
        {
          name: 'GPO Suppliers',
          path: '/app/supplier-profiles/list',
          disable: !is_supplier_profiles_enabled,
        },
        {
          name: 'Reconciliation',
          path: '/app/reconciliation/list',
          disable: !is_reconciliation_enabled,
        },
        {
          name: 'GPO Orders',
          path: '/app/gpo-orders/list',
          disable: !is_gpo_orders_enabled,
        },
        {
          name: 'Prices',
          path: '/app/prices/list',
          disable: !is_gpo_prices_enabled,
        },
        {
          name: 'Price Imports',
          path: '/app/price-imports/list',
          disable: !is_price_imports_enabled,
        },
        { name: 'Sales Lookup', path: '/app/sales-lookup/list', disable: !is_sales_lookup_enabled },
      ],
    },
    {
      name: 'BNPL',
      path: '/app/bnpl',
      icon: BnplIcon,
      disable: !enable_bnpl,
      children: [
        { name: 'Credit Facilities', path: '/app/bnpl/credit-facilities' },
        { name: 'Credit Utilizations', path: '/app/bnpl/credit-utilizations' },
        { name: 'Supplier Statements', path: '/app/bnpl/supplier-statements' },
      ],
    },
    {
      name: 'Invoice Scanning',
      path: '/app/invoice-scanning/list',
      icon: InvoiceScanIcon,
      disable: !enable_invoice_scanning,
    },
    {
      name: 'sidebar.userManagement',
      path: '/app/user-management',
      icon: UserManagementIcon,
    },
    {
      name: 'sidebar.permissions',
      path: '/app/preferences',
      icon: PreferencesIcon,
    },
    {
      name: 'sidebar.otp',
      path: '/app/sms-otp',
      icon: OtpIcon,
    },
    {
      name: 'sidebar.whatsNumbers',
      path: '/app/whats-numbers',
      icon: WhatsAppIcon,
      disable: !!is_cms_v1_11_0_enabled,
    },
    {
      name: 'sidebar.countries',
      path: '/app/countries',
      icon: CountiesIcon,
    },
    {
      name: 'sidebar.pocAgentCalls',
      path: '/app/poc-agent-calls',
      icon: CountiesIcon,
    },
  ];

  return sidebarItems;
};
